import type {
  SyncEntity,
  SyncManifest,
  SyncPullResponse,
  SyncPushResponse,
  SyncPushResult,
} from '@bssb/contracts';
import { v7 as uuidv7 } from 'uuid';
import { ApiError, apiRequest, hasAccessToken } from './api-client.ts';
import { isUnlocked } from './crypto.ts';
import { db, META_KEYS, type OutboxItem, readMeta, wipeLocalData, writeMeta } from './db.ts';
import { flushMediaQueue } from './media-queue.ts';
import { applyPull, pruneRemoved } from './pull-apply.ts';
import { reconcileReceipts } from './receipts.ts';
import { assertStorageAvailable } from './storage-quota.ts';

/**
 * Mesin sinkronisasi (T-P5-14, §7.2, FR-SYNC-04/06).
 *
 * Urutannya tetap: **outbox teks dulu, media menyusul, baru tarik delta**.
 * Kunjungan sah walau fotonya masih mengantre — server menautkannya belakangan
 * lewat `clientUuid` — sedangkan urutan sebaliknya membuat catatan lapangan
 * tertahan hanya karena unggahan foto lambat (FR-SYNC-07).
 */

const PUSH_BATCH_SIZE = 50;
const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 15 * 60 * 1000;
const PULL_PAGE_SIZE = 100;
const AUTO_INTERVAL_MS = 15 * 60 * 1000;

const PULL_ORDER: SyncEntity[] = [
  'customers',
  'loans',
  'addresses',
  'contacts',
  'collaterals',
  'paymentHistory',
  'visitHistory',
];

export interface SyncReport {
  startedAt: string;
  finishedAt: string;
  pushed: { applied: number; duplicate: number; conflict: number; rejected: number };
  media: { uploaded: number; failed: number };
  pulled: Partial<Record<SyncEntity, number>>;
  removed: number;
  wiped: boolean;
  errors: string[];
}

type Listener = (state: SyncState) => void;

export interface SyncState {
  running: boolean;
  lastReport: SyncReport | null;
  lastError: string | null;
}

/**
 * Kemajuan putaran berjalan (T-P7-04).
 *
 * Terpisah dari `SyncState` karena frekuensinya berbeda jauh: status berubah dua
 * kali per putaran, kemajuan berubah tiap halaman. Menggabungkannya akan membuat
 * indikator header ikut render ratusan kali saat unduhan awal berjalan.
 */
export interface SyncProgress {
  phase: 'push' | 'media' | 'pull' | 'done';
  entity: SyncEntity | null;
  /** Urutan entitas yang sedang ditarik, 1-based; 0 di luar fase pull. */
  entityIndex: number;
  entityCount: number;
  received: number;
}

type ProgressListener = (progress: SyncProgress) => void;
const progressListeners = new Set<ProgressListener>();

export function subscribeSyncProgress(listener: ProgressListener): () => void {
  progressListeners.add(listener);
  return () => progressListeners.delete(listener);
}

function emitProgress(progress: SyncProgress): void {
  for (const listener of progressListeners) listener(progress);
}

let state: SyncState = { running: false, lastReport: null, lastError: null };
const listeners = new Set<Listener>();
let timer: ReturnType<typeof setInterval> | undefined;
/** Satu putaran pada satu waktu; pemanggilan lain menumpang hasil yang sama. */
let active: Promise<SyncReport> | null = null;

export function subscribeSync(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

function emit(next: Partial<SyncState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener(state);
}

export function startAutoSync(): () => void {
  const trigger = () => {
    void run().catch(() => undefined);
  };

  globalThis.addEventListener('online', trigger);
  timer = setInterval(trigger, AUTO_INTERVAL_MS);
  trigger();

  return () => {
    globalThis.removeEventListener('online', trigger);
    if (timer) clearInterval(timer);
  };
}

export async function run(): Promise<SyncReport> {
  if (active) return active;

  active = execute().finally(() => {
    active = null;
  });

  return active;
}

async function execute(): Promise<SyncReport> {
  const startedAt = new Date().toISOString();
  const report: SyncReport = {
    startedAt,
    finishedAt: startedAt,
    pushed: { applied: 0, duplicate: 0, conflict: 0, rejected: 0 },
    media: { uploaded: 0, failed: 0 },
    pulled: {},
    removed: 0,
    wiped: false,
    errors: [],
  };

  if (!navigator.onLine || !hasAccessToken() || !isUnlocked()) {
    // Bukan kegagalan: perangkat memang sedang tidak bisa menyinkron.
    report.finishedAt = new Date().toISOString();
    return report;
  }

  emit({ running: true, lastError: null });

  try {
    const manifest = await apiRequest<SyncManifest>('/sync/manifest');

    // Perintah wipe dijalankan sebelum apa pun: perangkat ini sudah dianggap
    // tidak berhak lagi memegang data nasabah (FR-AUTH-10, FR-SYNC-11).
    if (manifest.wipeRequested) {
      await wipeLocalData();
      report.wiped = true;
      report.finishedAt = new Date().toISOString();
      emit({ running: false, lastReport: report });
      return report;
    }

    await writeMeta(META_KEYS.parameters, manifest.parameters);
    await writeMeta(META_KEYS.limits, manifest.limits);

    const storedVersion = await readMeta<number>(META_KEYS.schemaVersion);
    if (storedVersion !== undefined && storedVersion !== manifest.schemaVersion) {
      // Bentuk cache berubah; menyulamnya lebih berisiko daripada menariknya ulang.
      await resetCursors();
    }
    await writeMeta(META_KEYS.schemaVersion, manifest.schemaVersion);

    emitProgress({ phase: 'push', entity: null, entityIndex: 0, entityCount: 0, received: 0 });
    await pushOutbox(report, manifest.deviceId);

    emitProgress({ phase: 'media', entity: null, entityIndex: 0, entityCount: 0, received: 0 });
    const media = await flushMediaQueue();
    report.media = { uploaded: media.uploaded, failed: media.failed };

    await pullAll(report);

    // Nomor kuitansi resmi baru ada setelah pembayarannya tercatat; menukarnya di
    // sini membuat collector menemukannya sudah final saat membuka kuitansi,
    // bukan saat kebetulan membuka layarnya (T-P7-24).
    await reconcileReceipts().catch((error: unknown) =>
      report.errors.push(error instanceof Error ? error.message : String(error)),
    );

    await reportStatus(report);
    emitProgress({ phase: 'done', entity: null, entityIndex: 0, entityCount: 0, received: 0 });

    await writeMeta(META_KEYS.lastSyncAt, new Date().toISOString());
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    report.errors.push(message);
    emit({ lastError: message });
  }

  report.finishedAt = new Date().toISOString();
  emit({ running: false, lastReport: report });
  return report;
}

// --- push ----------------------------------------------------------------------

async function pushOutbox(report: SyncReport, deviceId: string | null): Promise<void> {
  const now = Date.now();

  for (;;) {
    const due = (
      await db.outbox.where('status').anyOf('PENDING', 'CONFLICT').sortBy('createdAt')
    ).filter((item) => item.status === 'PENDING' && Date.parse(item.nextAttemptAt) <= now);

    if (due.length === 0) return;

    const batch = due.slice(0, PUSH_BATCH_SIZE);
    const batchId = uuidv7();

    await db.outbox.bulkPut(batch.map((item) => ({ ...item, status: 'SENDING' as const })));

    try {
      const response = await apiRequest<SyncPushResponse>('/sync/push', {
        method: 'POST',
        body: {
          deviceId,
          batchId,
          items: batch.map((item) => ({
            entity: item.entity,
            op: item.op,
            clientUuid: item.clientUuid,
            payload: item.payload,
            occurredAt: item.createdAt,
          })),
          outboxLagSeconds: outboxLagSeconds(batch),
        },
      });

      await applyPushResults(batch, response, report);
    } catch (error) {
      // Kegagalan transport: seluruh batch kembali mengantre dengan backoff.
      await requeue(batch, error);
      throw error;
    }

    if (batch.length < PUSH_BATCH_SIZE) return;
  }
}

function outboxLagSeconds(items: OutboxItem[]): number {
  const oldest = items.reduce(
    (min, item) => Math.min(min, Date.parse(item.createdAt)),
    Number.POSITIVE_INFINITY,
  );

  return Number.isFinite(oldest) ? Math.max(Math.round((Date.now() - oldest) / 1000), 0) : 0;
}

async function applyPushResults(
  batch: OutboxItem[],
  response: SyncPushResponse,
  report: SyncReport,
): Promise<void> {
  const byUuid = new Map<string, SyncPushResult>(
    response.results.map((result) => [result.clientUuid, result]),
  );

  const settled: string[] = [];
  const updated: OutboxItem[] = [];

  for (const item of batch) {
    const result = byUuid.get(item.clientUuid);
    if (!result) {
      updated.push(nextAttempt(item, 'Server tidak mengembalikan status untuk item ini'));
      continue;
    }

    if (result.status === 'APPLIED' || result.status === 'DUPLICATE') {
      settled.push(item.clientUuid);
      if (result.serverId) await markVisitSynced(item, result.serverId);
      report.pushed[result.status === 'APPLIED' ? 'applied' : 'duplicate'] += 1;
      continue;
    }

    if (result.status === 'CONFLICT') {
      // Konflik tidak boleh dikirim ulang otomatis; menunggu tinjauan supervisor.
      report.pushed.conflict += 1;
      updated.push({
        ...item,
        status: 'CONFLICT',
        conflictId: result.conflictId,
        lastError: result.message ?? result.reason,
      });
      continue;
    }

    report.pushed.rejected += 1;
    updated.push({
      ...item,
      status: 'NEEDS_ATTENTION',
      lastError: result.message ?? result.reason,
    });
  }

  await db.transaction('rw', db.outbox, async () => {
    if (settled.length > 0) await db.outbox.bulkDelete(settled);
    if (updated.length > 0) await db.outbox.bulkPut(updated);
  });
}

async function markVisitSynced(item: OutboxItem, serverId: string): Promise<void> {
  if (item.entity !== 'visit') return;
  const local = await db.visitHistory.get(item.clientUuid);
  if (local) await db.visitHistory.put({ ...local, id: serverId, pending: false });
}

async function requeue(batch: OutboxItem[], error: unknown): Promise<void> {
  const message = error instanceof Error ? error.message : String(error);
  const permanent = error instanceof ApiError && error.isPermanent;

  await db.outbox.bulkPut(
    batch.map((item) =>
      permanent
        ? { ...item, status: 'NEEDS_ATTENTION' as const, lastError: message }
        : nextAttempt(item, message),
    ),
  );
}

/** Backoff eksponensial; setelah 5 percobaan item berhenti dicoba sendiri (§7.2). */
export function nextAttempt(item: OutboxItem, error: string): OutboxItem {
  const attempts = item.attempts + 1;
  const delay = Math.min(2 ** attempts * BASE_BACKOFF_MS, MAX_BACKOFF_MS);

  return {
    ...item,
    attempts,
    lastError: error,
    status: attempts >= MAX_ATTEMPTS ? 'NEEDS_ATTENTION' : 'PENDING',
    nextAttemptAt: new Date(Date.now() + delay).toISOString(),
  };
}

// --- pull ----------------------------------------------------------------------

async function pullAll(report: SyncReport): Promise<void> {
  await assertStorageAvailable();
  const cursors = (await readMeta<Record<string, string | null>>(META_KEYS.cursors)) ?? {};

  for (const [index, entity] of PULL_ORDER.entries()) {
    let cursor = cursors[entity] ?? null;
    let received = 0;

    for (;;) {
      emitProgress({
        phase: 'pull',
        entity,
        entityIndex: index + 1,
        entityCount: PULL_ORDER.length,
        received,
      });

      const query = new URLSearchParams({ entity, pageSize: String(PULL_PAGE_SIZE) });
      if (cursor) query.set('cursor', cursor);

      const page = await apiRequest<SyncPullResponse>(`/sync/pull?${query.toString()}`);
      await applyPull(entity, page.items);
      received += page.items.length;

      if (page.removedIds.length > 0) {
        report.removed += await pruneRemoved(entity, page.removedIds);
      }

      cursor = page.nextCursor ?? cursor;
      cursors[entity] = cursor;
      await writeMeta(META_KEYS.cursors, cursors);

      if (!page.hasNext) break;
    }

    report.pulled[entity] = received;
  }
}

async function resetCursors(): Promise<void> {
  await writeMeta(META_KEYS.cursors, {});
}

async function reportStatus(report: SyncReport): Promise<void> {
  const pendingItems = await db.outbox.where('status').equals('PENDING').count();
  const pendingMedia = await db.mediaQueue.where('status').anyOf('PENDING', 'UPLOADING').count();
  const oldest = await db.outbox.orderBy('createdAt').first();

  const query = new URLSearchParams({
    pendingItems: String(pendingItems),
    pendingMedia: String(pendingMedia),
    outboxLagSeconds: String(
      oldest ? Math.max(Math.round((Date.now() - Date.parse(oldest.createdAt)) / 1000), 0) : 0,
    ),
  });

  try {
    await apiRequest(`/sync/status?${query.toString()}`);
  } catch (error) {
    // Metrik bukan alasan menggagalkan sinkronisasi yang sudah berhasil.
    report.errors.push(error instanceof Error ? error.message : String(error));
  }
}

// --- mutasi lokal --------------------------------------------------------------

export interface WriteLocalInput {
  entity: OutboxItem['entity'];
  clientUuid?: string;
  payload: Record<string, unknown>;
  op?: OutboxItem['op'];
}

/**
 * Pola mutasi tunggal aplikasi lapangan (§10.2).
 *
 * Record final ditulis lebih dulu supaya layar langsung menampilkannya, lalu
 * item outbox mengantre. Keduanya dalam satu transaksi Dexie: kunjungan yang
 * tampil di layar tetapi tidak pernah terkirim adalah kegagalan paling mahal
 * yang bisa dialami collector.
 */
export async function writeLocal(
  input: WriteLocalInput,
  applyToCache?: () => Promise<void>,
): Promise<string> {
  const clientUuid = input.clientUuid ?? uuidv7();
  const now = new Date().toISOString();

  await db.transaction('rw', [db.outbox, db.visitHistory, db.mediaQueue], async () => {
    if (applyToCache) await applyToCache();

    await db.outbox.put({
      clientUuid,
      entity: input.entity,
      op: input.op ?? 'create',
      payload: { ...input.payload, clientUuid },
      status: 'PENDING',
      attempts: 0,
      nextAttemptAt: now,
      createdAt: now,
      lastError: null,
      serverId: null,
      conflictId: null,
    });
  });

  void run().catch(() => undefined);
  return clientUuid;
}

/** Kirim ulang item yang sudah diperbaiki collector dari layar "Antrean Bermasalah". */
export async function retryOutboxItem(clientUuid: string): Promise<void> {
  const item = await db.outbox.get(clientUuid);
  if (!item) return;

  await db.outbox.put({
    ...item,
    status: 'PENDING',
    attempts: 0,
    nextAttemptAt: new Date().toISOString(),
    lastError: null,
  });

  void run().catch(() => undefined);
}
